import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Sparkles, ArrowRight, CheckCircle2, PhoneCall, Building2, Home, MessageSquareText,
  Users, LayoutDashboard, Rocket, Play, Radio, Send, Droplets, BadgeCheck,
  KeyRound, CalendarDays, Waves, Receipt, Signal, Wifi, BatteryFull, Plus, Camera,
  Car, Clock, CalendarCheck, ChevronRight, Mic,
} from "lucide-react";
import { DemoActionCard, makeDemoAction } from "@/components/DemoActionCard";
import { scrollToEnd } from "@/lib/scroll";

/* ---------------------------------- copy ---------------------------------- */
const AUDIENCE = {
  single: {
    key: "single",
    badge: "For HOAs, multi-family & managed communities",
    h1a: "An AI concierge for every community.",
    h1b: "More capacity for every manager.",
    sub: "Residents call or text one number and get instant, cited answers straight from your governing documents, and every conversation makes it smarter. Ask Redline learns your community's rules, logs maintenance requests, and pulls in your board only when a person is truly needed. No app, no login, with a full community command center behind it.",
    cta: "Book a demo",
    points: [
      { icon: CheckCircle2, text: "Answers calls and texts from your own documents, always cited" },
      { icon: CheckCircle2, text: "Learns from every question, building your community's knowledge" },
      { icon: CheckCircle2, text: "Logs requests and escalates only what truly needs a person" },
    ],
  },
  portfolio: {
    key: "portfolio",
    badge: "For HOA management companies",
    h1a: "An AI concierge for every community.",
    h1b: "More capacity for every manager.",
    sub: "Give every association a brain that keeps learning. Residents call or text and get instant answers from each community's own documents, and every conversation builds that community's knowledge. Run it all from one dashboard, under your brand, adding a community in about a week.",
    cta: "Book a portfolio walkthrough",
    points: [
      { icon: Users, text: "A self-learning AI brain for every community you manage" },
      { icon: LayoutDashboard, text: "More doors per manager, since the repetitive calls answer themselves" },
      { icon: Rocket, text: "White-label, one dashboard, launch a community in about a week" },
    ],
  },
};

/* --------------------- looping hero conversations ------------------------- */
const HERO_THREADS = {
  single: [
    { who: "res", text: "What time does the pool close on weekends?" },
    { who: "bot", text: "The pool is open 6:00 AM to 10:00 PM on weekends, per Rules §4.2. Want me to send the gate code?", cite: "Community Rules · §4.2" },
    { who: "res", text: "Yes please. Also, when's the next board meeting?" },
    { who: "bot", text: "Code sent below. The next board meeting is Thursday, Aug 14 at 6:00 PM in the clubhouse." },
    { who: "res", text: "One more, is there guest parking for a party Saturday?" },
    { who: "bot", text: "Yes, guests can use the marked visitor spots near the clubhouse for up to 48 hours. Want me to log the event?" },
    { who: "res", text: "Perfect, thank you!" },
    { who: "bot", text: "Anytime. I'm here 24/7 if anything else comes up." },
  ],
  portfolio: [
    { who: "res", text: "How many communities can one manager handle with this?" },
    { who: "bot", text: "With the routine questions answered automatically, teams cover noticeably more doors per manager." },
    { who: "res", text: "Can each community keep its own documents and branding?" },
    { who: "bot", text: "Yes. Every association answers from its own docs, under your brand, all in one dashboard." },
    { who: "res", text: "How fast can we launch across the whole portfolio?" },
    { who: "bot", text: "Roughly a week per community, and each one launches without pulling your team off the phones. I can show the math for your portfolio." },
  ],
};

/* ------------------------------ scripted demo ----------------------------- */
const SCENARIOS = [
  { id: "leak", label: "Report a leak", icon: Droplets,
    value: { tag: "Instant maintenance tickets", title: "A leak becomes a work order", body: "Residents report an issue by text and it turns into a routed, logged maintenance ticket in seconds, so nothing slips through the cracks." },
    thread: [
    { who: "res", text: "There's water leaking from the unit above me in 407." },
    { who: "bot", text: "I'm on it. I've opened a maintenance ticket and flagged it to the on-site team so they can reach the unit above you.", action: makeDemoAction("open_ticket", { issue: "Water leak from unit above", location: "Unit 407" }) },
  ]},
  { id: "pool", label: "Pool hours", icon: Waves,
    value: { tag: "Answers from your documents", title: "Cited answers, straight from the rules", body: "Every answer comes from your own governing documents with the exact section cited, so residents get accurate info and your board is never second-guessed." },
    thread: [
    { who: "res", text: "What time does the pool close on weekends?" },
    { who: "bot", text: "The pool is open 6:00 AM to 10:00 PM on weekends, per Rules section 4.2. Want me to send the gate code?", cite: "Community Rules · §4.2" },
  ]},
  { id: "gate", label: "Get a gate code", icon: KeyRound,
    value: { tag: "Instant amenity access", title: "Amenity codes on demand", body: "Residents get time-limited access codes by text, so the office stops fielding gate, gym and pool requests all day long." },
    thread: [
    { who: "res", text: "Can you send me the pool gate code?" },
    { who: "bot", text: "Sent. Your temporary code is below, good for 24 hours.", action: makeDemoAction("send_access_code", { amenity: "pool" }) },
  ]},
  { id: "board", label: "Board meeting", icon: CalendarDays,
    value: { tag: "Always up to date", title: "Nobody misses a meeting", body: "Meeting dates, agendas and community events are answered instantly, keeping residents informed without another all-community email blast." },
    thread: [
    { who: "res", text: "When is the next board meeting?" },
    { who: "bot", text: "It's Thursday, August 14 at 6:00 PM in the clubhouse. The budget vote is on the agenda, and all residents are welcome." },
  ]},
  { id: "dues", label: "What dues cover", icon: Receipt,
    value: { tag: "Fewer billing calls", title: "Clear answers on dues", body: "Residents get a plain-English breakdown of what their dues cover, cited from the budget, cutting the repetitive finance questions to the office." },
    thread: [
    { who: "res", text: "What do my monthly dues actually cover?" },
    { who: "bot", text: "Your dues cover landscaping, pool service, common area insurance, and contributions to the reserve fund.", cite: "Annual Budget · 2025" },
  ]},
  { id: "parking", label: "Guest parking", icon: Car,
    value: { tag: "Fewer rule disputes", title: "Parking rules, on the spot", body: "Guest parking, permits and towing rules are answered the moment a resident asks, cited from your policies, so disputes don't land on a manager." },
    thread: [
    { who: "res", text: "Is there guest parking, and where?" },
    { who: "bot", text: "Yes. Guests can use the marked visitor spots near the clubhouse for up to 48 hours. Want me to issue a visitor pass?", cite: "Parking Rules · §3.1" },
  ]},
  { id: "quiet", label: "Quiet hours", icon: Clock,
    value: { tag: "Community harmony", title: "Rules everyone can find", body: "Quiet hours, pet rules and community standards are one text away, and residents can log a concern for the team without calling anyone." },
    thread: [
    { who: "res", text: "What are the quiet hours here?" },
    { who: "bot", text: "Quiet hours are 10:00 PM to 7:00 AM every day, per the CC&Rs. Want me to log a noise concern for the team?", cite: "CC&Rs · §5.3" },
  ]},
  { id: "clubhouse", label: "Reserve clubhouse", icon: CalendarCheck,
    value: { tag: "Self-serve reservations", title: "Book amenities by text", body: "Residents start clubhouse and amenity reservations right from the chat, so bookings stop piling up on a manager's desk." },
    thread: [
    { who: "res", text: "How do I reserve the clubhouse for a birthday party?" },
    { who: "bot", text: "I can start that for you. The clubhouse is $75 for a 4 hour block with a refundable deposit. What date works best?" },
  ]},
];

const LIVE_CHIPS = [
  "What are the quiet hours?",
  "How do I get a replacement key fob?",
  "Is there guest parking?",
  "How do I reserve the clubhouse?",
  "What's the pet policy?",
  "When is trash pickup?",
  "How do I pay my dues online?",
  "Can I install solar panels?",
];

const SCENARIO_CAT = {
  leak: "Maintenance", pool: "Amenities", gate: "Access", board: "Community",
  dues: "Billing", parking: "Parking", quiet: "Rules", clubhouse: "Amenities",
};

const readTime = (text = "") => Math.min(4800, Math.max(2000, text.length * 42));

/* --------------------------------- bubbles -------------------------------- */
function Bubble({ who, text, action, cite }) {
  const mine = who === "res";
  return (
    <motion.div initial={{ opacity: 0, y: 10, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-3.5 py-2 text-[13px] leading-snug shadow-sm ${mine
        ? "bg-gradient-to-br from-redline-500 to-redline-600 text-white rounded-2xl rounded-br-sm"
        : "bg-white text-ink rounded-2xl rounded-bl-sm border border-sand-200"}`}>
        {text}
        {action && <DemoActionCard action={action} />}
        {cite && (
          <div className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-redline-600 bg-redline-50 rounded-md px-2 py-1 w-fit">
            <MessageSquareText className="h-3 w-3" strokeWidth={2} /> {cite}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Typing() {
  return (
    <motion.div className="flex justify-start"
      initial={{ opacity: 0, y: 8, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.25 }}>
      <div className="bg-white border border-sand-200 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5 shadow-sm">
        {[0, 1, 2].map((i) => (
          <motion.span key={i} className="h-2 w-2 rounded-full bg-ink-600"
            animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.16 }} />
        ))}
      </div>
    </motion.div>
  );
}

/* --------------------------- realistic phone ------------------------------ */
function Phone({ messages = [], typing, live, autoScroll, input, setInput, onSend, sending, onCall, title = "Ask Redline", testid = "concierge-phone" }) {
  const chatRef = useRef(null);
  useEffect(() => { scrollToEnd(chatRef.current, true); }, [messages, typing]);
  const scrolls = live || autoScroll;

  // voice-to-text dictation (live mode only)
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const baseTextRef = useRef("");
  const speechSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const toggleDictation = () => {
    if (!speechSupported) return;
    if (listening) { try { recogRef.current?.stop(); } catch (e) {} return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    baseTextRef.current = input ? input.trim() + " " : "";
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      setInput?.(baseTextRef.current + txt);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") toast.error("Microphone permission denied.");
    };
    rec.onend = () => setListening(false);
    recogRef.current = rec;
    try { rec.start(); setListening(true); } catch (e) { setListening(false); }
  };
  useEffect(() => () => { try { recogRef.current?.stop(); } catch (e) {} }, []);

  return (
    <div className="relative mx-auto w-fit" data-testid={testid}>
      <div className="absolute -inset-8 bg-redline-500/15 blur-3xl rounded-full -z-10" />
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-[318px] rounded-[3rem] bg-ink p-[10px] shadow-[0_28px_70px_rgb(25,22,20,0.26)] ring-1 ring-white/10">
        <div className="absolute -left-[3px] top-28 h-14 w-[3px] rounded-l bg-ink-700" />
        <div className="absolute -right-[3px] top-24 h-20 w-[3px] rounded-r bg-ink-700" />
        <div className="relative rounded-[2.4rem] bg-sand-50 overflow-hidden">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 h-6 w-24 bg-ink rounded-full z-20" />
          <div className="bg-redline-500 text-white pt-2.5">
            <div className="flex items-center justify-between px-5 text-[11px] font-semibold">
              <span>9:41</span>
              <div className="flex items-center gap-1"><Signal className="h-3 w-3" strokeWidth={2.5} /><Wifi className="h-3 w-3" strokeWidth={2.5} /><BatteryFull className="h-3.5 w-3.5" strokeWidth={2} /></div>
            </div>
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="relative h-9 w-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
                <MessageSquareText className="h-4 w-4 text-white" strokeWidth={1.75} />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-redline-500" />
              </div>
              <div className="leading-tight flex-1 min-w-0">
                <div className="text-sm font-bold flex items-center gap-1"><span className="truncate">{title}</span> <BadgeCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2} /></div>
                <div className="text-[10px] text-white/80 truncate">Active now · replies instantly</div>
              </div>
              <button type="button" onClick={onCall} data-testid="phone-call-btn" title="Tap to talk live"
                className="relative h-8 w-8 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center backdrop-blur transition-colors shrink-0 cursor-pointer">
                <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                <PhoneCall className="h-4 w-4 relative" strokeWidth={2} />
              </button>
            </div>
          </div>
          <div ref={chatRef} className={`relative h-[480px] ${scrolls ? "overflow-y-auto" : "overflow-hidden"} flex flex-col justify-end gap-2 px-3.5 pt-3 pb-3 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.04)_1px,transparent_0)] [background-size:16px_16px]`}>
            <div className="text-center"><span className="text-[9px] text-ink-400 bg-sand-200/60 rounded-full px-2 py-0.5">Today 9:41 AM</span></div>
            <AnimatePresence mode="popLayout">
              {messages.map((m, i) => <Bubble key={i} {...m} />)}
            </AnimatePresence>
            {typing && <Typing />}
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-sand-50 to-transparent" />
          </div>
          {live ? (
            <form onSubmit={(e) => { e.preventDefault(); onSend?.(); }} className="border-t border-sand-200 bg-white px-3 py-2.5 flex items-center gap-2">
              <Plus className="h-4 w-4 text-ink-400 shrink-0" strokeWidth={2} />
              <input value={input} onChange={(e) => setInput?.(e.target.value)} disabled={sending} data-testid="live-input"
                placeholder={listening ? "Listening…" : "Ask anything about the community"}
                className="flex-1 rounded-full bg-sand-100 border border-sand-200 px-3.5 py-2 text-[12px] text-ink outline-none focus:border-redline-400 disabled:opacity-60" />
              {speechSupported && (
                <button type="button" onClick={toggleDictation} disabled={sending} data-testid="live-mic"
                  title={listening ? "Stop dictation" : "Speak instead of typing"}
                  className={`relative h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-40 ${listening ? "bg-redline-500 text-white" : "bg-sand-200 text-ink-600 hover:bg-sand-300"}`}>
                  {listening && <span className="absolute inset-0 rounded-full bg-redline-400/50 animate-ping" />}
                  <Mic className="h-4 w-4 relative" strokeWidth={2} />
                </button>
              )}
              <button type="submit" disabled={sending || !input?.trim()} data-testid="live-send"
                className="h-8 w-8 rounded-full bg-redline-500 text-white flex items-center justify-center shrink-0 disabled:opacity-40 hover:bg-redline-600 transition-colors">
                <Send className="h-4 w-4" strokeWidth={2} />
              </button>
            </form>
          ) : (
            <div className="border-t border-sand-200 bg-white px-3 py-2.5 flex items-center gap-2">
              <Plus className="h-4 w-4 text-ink-400 shrink-0" strokeWidth={2} />
              <div className="flex-1 rounded-full bg-sand-100 border border-sand-200 px-3.5 py-2 text-[12px] text-ink-400">Text message…</div>
              <Camera className="h-4 w-4 text-redline-500 shrink-0" strokeWidth={1.75} />
            </div>
          )}
          <div className="flex justify-center py-2 bg-white"><div className="h-1 w-28 rounded-full bg-ink/20" /></div>
        </div>
      </motion.div>
    </div>
  );
}

/* --------------------- looping auto conversation -------------------------- */
function AutoConversation({ thread, onCall }) {
  const [msgs, setMsgs] = useState([]);
  const [typing, setTyping] = useState(false);
  const timers = useRef([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    clear();
    if (reduce) { setMsgs(thread); setTyping(false); return clear; }
    const at = (t, fn) => timers.current.push(setTimeout(fn, t));
    const run = () => {
      setMsgs([]); setTyping(false);
      let t = 800;
      thread.forEach((m) => {
        if (m.who === "bot") { at(t, () => setTyping(true)); t += 1500; }
        at(t, () => { setMsgs((p) => [...p, m]); setTyping(false); });
        t += readTime(m.text);
      });
      at(t + 4000, run); // loop the whole conversation
    };
    run();
    return clear;
  }, [thread, reduce]);

  return <Phone messages={msgs} typing={typing} autoScroll onCall={onCall} testid="hero-phone" />;
}

/* ---------------------------- scenario button ----------------------------- */
function ScenarioButton({ item, sub, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`group relative w-full text-left rounded-2xl border transition-all duration-300 flex items-center gap-3 p-3 hover:-translate-y-0.5
        ${active
          ? "border-ink bg-ink text-white shadow-[0_14px_34px_rgb(25,22,20,0.20)]"
          : "border-sand-200/70 bg-white hover:border-redline-200 hover:shadow-[0_10px_26px_rgb(25,22,20,0.08)]"}`}>
      <span className={`shrink-0 grid place-items-center h-10 w-10 rounded-xl transition-colors duration-300
        ${active ? "bg-white/12 text-redline-400" : "bg-redline-50 text-redline-500 group-hover:bg-redline-500 group-hover:text-white"}`}>
        <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block leading-snug break-words ${sub ? "text-[13.5px] font-bold truncate" : "text-[12.5px] font-semibold line-clamp-2"}`}>{item.label}</span>
        {sub && <span className={`block mt-0.5 text-[9.5px] font-bold uppercase tracking-[0.13em] ${active ? "text-white/45" : "text-ink-400"}`}>{sub}</span>}
      </span>
      <ChevronRight className={`h-4 w-4 shrink-0 transition-all duration-300
        ${active ? "text-white/70 translate-x-0 opacity-100" : "text-redline-400 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"}`} strokeWidth={2.75} />
    </button>
  );
}

/* ------------------------------- demo stage ------------------------------- */
/* subtle "signal" backdrop, concentric rings + soft waves radiating from behind the phone */
function DemoBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* soft radial glow behind the phone */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full bg-redline-50/70 blur-3xl" />
      {/* static concentric rings */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        {[360, 560, 780, 1020].map((d, i) => (
          <div key={d} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-redline-100"
            style={{ height: d, width: d, opacity: 0.55 - i * 0.11 }} />
        ))}
      </div>
      {/* animated signal waves emanating from the phone */}
      {[0, 1, 2].map((i) => (
        <motion.div key={i} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-redline-200"
          style={{ height: 340, width: 340 }}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 2.6, opacity: [0, 0.4, 0] }}
          transition={{ duration: 6, repeat: Infinity, delay: i * 2, ease: "easeOut" }} />
      ))}
    </div>
  );
}


export function DemoStage({ onVoice }) {
  const [mode, setMode] = useState("watch"); // watch | live
  const [active, setActive] = useState("leak");
  const [step, setStep] = useState(0);
  const [typing, setTyping] = useState(false);
  const timers = useRef([]);
  const reduce = useReducedMotion();

  const [liveMsgs, setLiveMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const sessionRef = useRef(`web_${Date.now()}_${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const { data } = await api.get("/webdemo/config"); if (!cancelled) setCfg(data); } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  useEffect(() => {
    if (mode !== "watch") { clearTimers(); return; }
    clearTimers();
    setStep(0); setTyping(false);
    const thread = SCENARIOS.find((s) => s.id === active)?.thread || [];
    if (reduce) { setStep(thread.length); return; }
    const at = (t, fn) => timers.current.push(setTimeout(fn, t));
    let t = 700;
    thread.forEach((m, i) => {
      if (m.who === "bot") { at(t, () => setTyping(true)); t += 1600; }
      at(t, () => { setStep(i + 1); setTyping(false); });
      t += readTime(m.text);
    });
    const nextIdx = (SCENARIOS.findIndex((s) => s.id === active) + 1) % SCENARIOS.length;
    at(t + 3400, () => setActive(SCENARIOS[nextIdx].id));
    return clearTimers;
    // eslint-disable-next-line
  }, [active, mode, reduce]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setLiveMsgs((p) => [...p, { who: "res", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/webdemo/chat", { session_id: sessionRef.current, message: msg });
      setLiveMsgs((p) => [...p, { who: "bot", text: data.reply, action: data.action || undefined }]);
    } catch {
      setLiveMsgs((p) => [...p, { who: "bot", text: "The demo's taking a quick breather — give it a moment and try again, or book a demo to see it on your community's own docs." }]);
    } finally { setSending(false); }
  };

  const thread = SCENARIOS.find((s) => s.id === active)?.thread || [];
  const phoneMessages = mode === "watch" ? thread.slice(0, step) : liveMsgs;

  const watchLeft = SCENARIOS.slice(0, 4);
  const watchRight = SCENARIOS.slice(4, 8);
  const liveLeft = LIVE_CHIPS.slice(0, 4).map((t, i) => ({ id: `l${i}`, label: t, icon: MessageSquareText }));
  const liveRight = LIVE_CHIPS.slice(4, 8).map((t, i) => ({ id: `r${i}`, label: t, icon: MessageSquareText }));

  const col = (watchItems, liveItems, side) =>
    (mode === "watch" ? watchItems : liveItems).map((item) => (
      <ScenarioButton key={item.id} item={item}
        sub={mode === "watch" ? SCENARIO_CAT[item.id] : null}
        active={mode === "watch" && item.id === active}
        testid={`scenario-${side}-${item.id}`}
        onClick={() => (mode === "watch" ? setActive(item.id) : send(item.label))} />
    ));

  return (
    <div className="relative overflow-hidden bg-white rounded-[2rem] border border-ink/[0.06] shadow-[0_24px_60px_rgb(25,22,20,0.10)] px-4 sm:px-8 py-8 sm:py-10">
      <DemoBackdrop />
      <div className="relative z-10">
      {/* top toggle */}
      <div className="flex justify-center mb-9">
        <div className="inline-flex rounded-full bg-sand-100 border border-sand-200 p-1 text-[13px] font-semibold">
          <button data-testid="demo-mode-watch" onClick={() => setMode("watch")}
            className={`rounded-full px-5 py-2 flex items-center gap-1.5 transition-colors ${mode === "watch" ? "bg-ink text-white" : "text-ink-600 hover:text-ink"}`}>
            <Play className="h-3.5 w-3.5" strokeWidth={2.25} /> Watch a demo
          </button>
          <button data-testid="demo-mode-live" onClick={() => setMode("live")}
            className={`rounded-full px-5 py-2 flex items-center gap-1.5 transition-colors ${mode === "live" ? "bg-redline-500 text-white" : "text-ink-600 hover:text-ink"}`}>
            <span className="relative flex h-2 w-2"><span className={`absolute inline-flex h-full w-full rounded-full ${mode === "live" ? "bg-white/70" : "bg-redline-400"} opacity-75 animate-ping`} /><span className={`relative inline-flex rounded-full h-2 w-2 ${mode === "live" ? "bg-white" : "bg-redline-500"}`} /></span>
            Try it live
          </button>
        </div>
      </div>

      {/* stage: scenarios hugging the phone on both sides */}
      <div className="flex flex-col lg:flex-row items-stretch justify-center gap-4 lg:gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-col lg:justify-center gap-3 w-full lg:w-[232px]">
          {col(watchLeft, liveLeft, "l")}
        </div>

        <Phone messages={phoneMessages} typing={mode === "watch" ? typing : sending} live={mode === "live"} autoScroll
          title={mode === "live" ? (cfg?.persona || "Ask Redline") : "Ask Redline"}
          input={input} setInput={setInput} onSend={() => send()} sending={sending} onCall={onVoice} testid="demo-phone" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-col lg:justify-center gap-3 w-full lg:w-[232px]">
          {col(watchRight, liveRight, "r")}
        </div>
      </div>

      {/* value band (watch mode): describes what you're seeing */}
      {mode === "watch" && (() => {
        const sc = SCENARIOS.find((s) => s.id === active);
        if (!sc) return null;
        return (
          <div className="mt-8">
            <AnimatePresence mode="wait">
              <motion.div key={sc.id} data-testid={`demo-value-${sc.id}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full rounded-2xl bg-gradient-to-br from-sand-50 to-white border border-sand-200 px-5 py-4 sm:px-6 sm:py-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  <div className="sm:w-72 sm:shrink-0">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-redline-700 bg-redline-50 rounded-full px-2.5 py-1">
                      <sc.icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {sc.value.tag}
                    </span>
                    <h3 className="font-heading text-base sm:text-lg font-extrabold tracking-tight text-ink leading-snug mt-2">{sc.value.title}</h3>
                  </div>
                  <p className="flex-1 text-[13px] sm:text-sm text-ink-700 leading-relaxed">{sc.value.body}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        );
      })()}

      {/* voice CTA underneath the phone */}
      <div className="mt-9 flex justify-center">
        <button onClick={onVoice} data-testid="voice-invite"
          className="group inline-flex items-center gap-2.5 rounded-full bg-sand-50 border border-sand-200 px-5 py-3 text-[13.5px] font-semibold text-ink-700 hover:border-redline-300 hover:text-redline-600 transition-colors text-left">
          <span className="grid place-items-center h-7 w-7 rounded-full bg-redline-500 text-white shadow-sm group-hover:scale-105 transition-transform shrink-0"><PhoneCall className="h-3.5 w-3.5" strokeWidth={2.5} /></span>
          Press the call button to hear how it handles a resident, live.
        </button>
      </div>
      </div>
    </div>
  );
}

/* -------------------------------- hero ------------------------------------ */
export function ConciergeHero({ onVoice, ctaAttrs = { href: "#demo" } }) {
  const [audience, setAudience] = useState("portfolio");
  const a = AUDIENCE[audience];

  return (
    <header className="relative overflow-hidden">
      <div className="grain absolute inset-0 opacity-[0.05]" />
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center py-14 lg:py-20 relative">
        <div className="lg:col-span-6 xl:col-span-7 flex flex-col items-start gap-7">
          <div role="tablist" aria-label="Choose your audience" className="inline-flex bg-sand-200/50 p-1.5 rounded-full text-sm font-semibold">
            {[["single", "For one community", Home], ["portfolio", "For HOA management companies", Building2]].map(([id, label, Icon]) => (
              <button key={id} role="tab" aria-selected={audience === id} data-testid={`aud-${id}`} onClick={() => setAudience(id)}
                className="relative rounded-full px-4 py-2 flex items-center gap-1.5 transition-colors">
                {audience === id && <motion.span layoutId="aud-pill" className="absolute inset-0 bg-white rounded-full shadow-sm" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className={`relative flex items-center gap-1.5 ${audience === id ? "text-ink" : "text-ink-500"}`}><Icon className="h-3.5 w-3.5" /> {label}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={a.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="flex flex-col items-start gap-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-xs font-bold px-3 py-1.5"><Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} /> {a.badge}</div>
              <h1 className="font-heading text-[7vw] sm:text-5xl lg:text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.08] sm:leading-[1.05] text-ink">
                {a.h1a}<br /><span className="text-redline-500">{a.h1b}</span>
              </h1>
              <p className="text-lg text-ink-700 leading-relaxed max-w-xl">{a.sub}</p>
              <ul className="flex flex-col gap-3.5">
                {a.points.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-[17px] text-ink-700"><p.icon className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={2} /> {p.text}</li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <a {...ctaAttrs} data-testid="hero-primary-cta" className="rounded-full bg-redline-500 text-white font-bold tracking-wide px-8 py-4 flex items-center gap-2 shadow-lg hover:shadow-xl hover:bg-redline-600 hover:-translate-y-0.5 transition-all">
                  {a.cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </a>
                <button onClick={onVoice} data-testid="hero-voice-btn"
                  className="group relative rounded-full bg-ink text-white font-bold pl-4 pr-6 py-4 inline-flex items-center gap-3 overflow-hidden hover:-translate-y-0.5 transition-transform shadow-lg">
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-redline-500/0 via-redline-500/20 to-redline-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative grid place-items-center h-8 w-8 rounded-full bg-redline-500">
                    <span className="absolute inset-0 rounded-full bg-redline-500 animate-ping opacity-60" />
                    <span className="relative flex items-end gap-[2px] h-3.5">
                      {[0, 1, 2, 3].map((i) => (
                        <motion.span key={i} className="w-[2.5px] rounded-full bg-white"
                          animate={{ height: ["25%", "100%", "40%", "85%", "30%"] }}
                          transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut", delay: i * 0.13 }}
                          style={{ height: "40%" }} />
                      ))}
                    </span>
                  </span>
                  <span className="relative flex flex-col items-start gap-1 leading-tight py-0.5">
                    <span className="text-[10px] font-bold text-white/55 tracking-[0.14em] uppercase">Voice concierge</span>
                    <span className="text-[16px] leading-none">Talk to it live</span>
                  </span>
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* looping conversation device */}
        <div className="lg:col-span-6 xl:col-span-5 flex justify-center lg:justify-end">
          <AutoConversation thread={HERO_THREADS[audience]} onCall={onVoice} />
        </div>
      </div>
    </header>
  );
}

/* -------------------------- demo section ---------------------------------- */
export function DemoSection({ onVoice }) {
  return (
    <section id="demo" className="scroll-mt-20 py-20 lg:py-24">
      <div className="max-w-2xl mx-auto px-6 text-center mb-12">
        <div className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-xs font-bold px-3 py-1.5 mb-4"><Radio className="h-3.5 w-3.5" strokeWidth={1.75} /> Live and interactive</div>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">Meet your community's concierge.</h2>
        <p className="mt-4 text-lg text-ink-700">Watch a real scenario, type your own question, or talk to it out loud. It's the real thing, answering from a demo community's documents.</p>
      </div>
      <div className="w-full max-w-5xl mx-auto px-4 lg:px-6">
        <DemoStage onVoice={onVoice} />
      </div>
    </section>
  );
}

/* ---------------- compact live phone (hero use), real /webdemo chat ---------------- */
const HERO_CHIPS = ["What are the pool hours?", "Is there guest parking?", "How do I reserve the clubhouse?", "When is trash pickup?"];

export function LivePhone({ onVoice, chips = HERO_CHIPS, testid = "live-phone" }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const sessionRef = useRef(`web_${Date.now()}_${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    let cancelled = false;
    api.get("/webdemo/config").then(({ data }) => {
      if (cancelled) return;
      setCfg(data);
      setMsgs([{ who: "bot", text: `Hi! I'm ${data?.persona || "the concierge"} for ${data?.community || "a demo community"}. Ask me anything a resident would, pool hours, guest parking, dues, a leak.` }]);
    }).catch(() => setMsgs([{ who: "bot", text: "Hi! I'm the concierge for a demo community. Ask me anything a resident would, pool hours, guest parking, dues, a leak." }]));
    return () => { cancelled = true; };
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMsgs((p) => [...p, { who: "res", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/webdemo/chat", { session_id: sessionRef.current, message: msg });
      setMsgs((p) => [...p, { who: "bot", text: data.reply, action: data.action || undefined }]);
    } catch {
      setMsgs((p) => [...p, { who: "bot", text: "The demo's taking a quick breather — give it a moment and try again, or book a demo to see it on your community's own docs." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid={testid}>
      <Phone messages={msgs} typing={sending} live input={input} setInput={setInput} onSend={() => send()} sending={sending}
        onCall={onVoice} title={cfg?.persona || "Ask Redline"} testid={`${testid}-device`} />
      <div className="flex flex-wrap justify-center gap-2 max-w-[360px]" data-testid={`${testid}-chips`}>
        {chips.map((c, i) => (
          <button key={c} type="button" onClick={() => send(c)} disabled={sending} data-testid={`${testid}-chip-${i}`}
            className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-[11.5px] font-semibold text-white/80 hover:bg-white/[0.12] hover:text-white transition-colors disabled:opacity-50">{c}</button>
        ))}
      </div>
    </div>
  );
}
